import { configureQzSecurity, mapQzConnectionError } from './qzSecurity';
import {
  PrinterNotFoundError,
  QzNotRunningError,
  QzPermissionError,
  QzPrintError,
} from './qzPrinterErrors';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'qzPrinterService';

export {
  PrinterNotFoundError,
  QzNotRunningError,
  QzPermissionError,
  QzPrintError,
} from './qzPrinterErrors';

let qzModule = null;
let connectPromise = null;

const loadQz = async () => {
  if (!qzModule) {
    const mod = await import('qz-tray');
    qzModule = mod.default || mod;
  }
  return qzModule;
};

const throwMappedConnectionError = error => {
  const mapped = mapQzConnectionError(error);
  if (mapped.code === 'QZ_NOT_RUNNING') {
    throw new QzNotRunningError(mapped.userMessage);
  }
  if (mapped.code === 'QZ_PERMISSION') {
    throw new QzPermissionError(mapped.userMessage);
  }
  throw error;
};

/**
 * @param {string|null} authToken
 */
export const connect = async (authToken = null) => {
  const qz = await loadQz();
  if (qz.websocket.isActive()) return qz;

  if (!connectPromise) {
    connectPromise = (async () => {
      await configureQzSecurity(qz, authToken);
      try {
        logInfo(LOG_CONTEXT, 'connect start');
        await qz.websocket.connect();
        logInfo(LOG_CONTEXT, 'connect success');
        return qz;
      } catch (error) {
        logError(LOG_CONTEXT, 'connect failed', error);
        throwMappedConnectionError(error);
        throw error;
      } finally {
        connectPromise = null;
      }
    })();
  }

  return connectPromise;
};

export const disconnect = async () => {
  const qz = await loadQz();
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
    logInfo(LOG_CONTEXT, 'disconnected');
  }
};

export const isConnected = async () => {
  const qz = await loadQz();
  return qz.websocket.isActive();
};

export const listPrinters = async (authToken = null) => {
  const qz = await connect(authToken);
  const printers = await qz.printers.find();
  logInfo(LOG_CONTEXT, 'listPrinters', { count: printers?.length ?? 0 });
  return printers || [];
};

/**
 * @param {string} printerName
 * @param {string[]} printers
 */
export const findPrinter = (printerName, printers) => {
  if (printerName && printers.includes(printerName)) return printerName;
  throw new PrinterNotFoundError(printerName || '(not configured)');
};

/**
 * @param {string} printerName
 * @param {string} rawCommands
 * @param {string} jobName
 * @param {string|null} authToken
 */
export const printRaw = async (printerName, rawCommands, jobName, authToken = null) => {
  const qz = await connect(authToken);
  const config = qz.configs.create(printerName, { jobName });
  const payload = [
    {
      type: 'raw',
      format: 'command',
      flavor: 'plain',
      data: rawCommands,
    },
  ];
  try {
    logInfo(LOG_CONTEXT, 'printRaw start', { printerName, jobName });
    await qz.print(config, payload);
    logInfo(LOG_CONTEXT, 'printRaw success', { printerName, jobName });
  } catch (error) {
    logError(LOG_CONTEXT, 'printRaw failed', error);
    throw new QzPrintError('Printing failed. Try again or use Print Preview.', error);
  }
};

/** Test helpers */
export const resetQzModuleForTests = () => {
  qzModule = null;
  connectPromise = null;
};

export const setQzModuleForTests = mod => {
  qzModule = mod;
  connectPromise = null;
};
